import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { Check, Info, Loader2, AlertCircle } from 'lucide-react';
import {
  Badge,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@scio3/ui';
import { ALL_PLAN_FEATURES_QUERY } from '@scio3/graphql-client';
import { BILLING_INTERVAL } from '@scio3/shared-types';
import type { BillingInterval } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateMedium } from '@/lib/format-date';
import { getCurrencySymbol, getPlanDisplayName } from '../utils/plan-helpers';

/** Feature from backend PlanFeatures.features array */
interface PlanFeatureItem {
  name?: string;
  description?: string;
  enable?: boolean;
  checked?: boolean;
  isHide?: boolean;
}

/** Plan pricing entry from backend PlanFeatures.plans JSONObject */
interface PlanPriceEntry {
  price?: number;
  name?: string;
  alias?: string;
  text?: string;
  discount_text?: string;
  discount?: number;
  stripe_plan_id?: string;
}

/** Single plan feature document from backend */
interface PlanFeatureNode {
  _id: string;
  alias?: string;
  name?: string;
  description?: string;
  order?: number;
  plans?: Record<string, Record<string, PlanPriceEntry>>;
  features?: PlanFeatureItem[];
  isActive?: boolean;
  learnMoreURL?: string;
}

interface AllPlanFeaturesResponse {
  planFeatures: {
    page: {
      edges: Array<{ node: PlanFeatureNode }>;
    };
  };
}

type PlanComparisonTableProps = {
  value: string;
  onChange: (planFeature: string) => void;
  /** Called when user selects the Free plan — opens downgrade flow with validation */
  onSelectFree?: () => void;
  /** True while the Free-plan handler is in flight — disables the Free button to prevent duplicate clicks. */
  selectingFree?: boolean;
  currency?: string;
  currentPlan?: string;
  /** Recommended plan alias (default: proPlus) */
  recommendedPlan?: string;
  /** Minimum plan required — hides plans below this level (by backend order field) */
  minPlanRequired?: string;
  /** Hide the Free plan bar — true when the user is already on Free (SP45, scio1 parity) */
  hideFreePlanOption?: boolean;
  /** Billing interval — drives which price entry (monthly vs yearly) is shown on each card */
  billingInterval?: BillingInterval;
  /** Optional callback to swap the billing interval (used by the Save-10% badge so users
   *  can flip Monthly → Yearly without scrolling back up to the toggle). */
  onBillingIntervalChange?: (next: BillingInterval) => void;
};

/**
 * Plan comparison cards matching scio1's step 2 layout.
 * Fetches all plan features from backend and renders as a card grid.
 */
export function PlanComparisonTable({
  value,
  onChange,
  onSelectFree,
  selectingFree = false,
  currency = 'usd',
  currentPlan,
  recommendedPlan = 'proPlus',
  minPlanRequired,
  hideFreePlanOption = false,
  billingInterval = BILLING_INTERVAL.MONTHLY,
  onBillingIntervalChange,
}: PlanComparisonTableProps) {
  const { t } = useTranslation();
  const isTrial = useAuthStore((s) => s.company?.payment?.isTrial ?? false);
  const trialEndDate = useAuthStore((s) => s.company?.payment?.trialEndDate);
  const isYearly =
    billingInterval === BILLING_INTERVAL.YEARLY ||
    billingInterval === BILLING_INTERVAL.YEARLY_FREEDEVICE;

  const { data, loading } = useQuery<AllPlanFeaturesResponse>(ALL_PLAN_FEATURES_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const { plans, freePlan } = useMemo(() => {
    const edges = data?.planFeatures?.page?.edges ?? [];
    const allPlans = edges.map((e) => e.node);
    const free = allPlans.find((n) => n.alias === 'free') ?? null;
    // When minPlanRequired is set, filter out plans below that level (by order field)
    const minOrder = minPlanRequired
      ? (allPlans.find((n) => n.alias === minPlanRequired)?.order ?? 0)
      : 0;
    const paid = allPlans
      // Only show active/visible plans. Retired tiers (e.g. legacy `pro`, `daily`) carry
      // isActive=false in the catalog and must never render as a selectable card — even for
      // an account currently ON that tier. A legacy-pro account keeps its plan implicitly
      // (selectedPlan defaults to `pro` in useSubscriptionWizard) and upgrades by picking
      // Pro Plus; it never needs a `pro` card on screen.
      .filter((n) => n.isActive !== false && n.alias !== 'free' && (n.order ?? 0) >= minOrder)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return { plans: paid, freePlan: free };
  }, [data, minPlanRequired]);

  // Get the price entry for a plan in the current currency, matching the active billing interval
  const getPriceEntry = (plan: PlanFeatureNode): PlanPriceEntry | undefined => {
    const currencyPlans = plan.plans?.[currency];
    if (!currencyPlans) return undefined;
    const matchToken = isYearly ? 'yearly' : 'monthly';
    const matchedKey = Object.keys(currencyPlans).find((k) => k.includes(matchToken));
    if (matchedKey) return currencyPlans[matchedKey];
    // Fallback: if yearly was requested but no yearly entry exists, try monthly
    const fallbackKey = Object.keys(currencyPlans).find((k) => k.includes('monthly'));
    return fallbackKey ? currencyPlans[fallbackKey] : undefined;
  };

  // Visible features for a plan — matching scio1: show all non-hidden features
  // Uses feature.description for display text (not feature.name)
  const getFeatures = (plan: PlanFeatureNode): PlanFeatureItem[] => {
    return (plan.features ?? []).filter((f) => !f.isHide && f.description?.trim());
  };

  // Free plan limitation features (unchecked = limitations like "3 screens limit")
  const freePlanLimitations = useMemo(() => {
    return (freePlan?.features ?? [])
      .filter((f) => !f.isHide && !f.checked && f.description?.trim())
      .slice(0, 4)
      .map((f) => f.description ?? '');
  }, [freePlan]);

  // Free plan included features (checked = what's included, for tooltip)
  const freePlanIncluded = useMemo(() => {
    return (freePlan?.features ?? [])
      .filter((f) => !f.isHide && f.checked && f.description?.trim())
      .map((f) => f.description ?? '');
  }, [freePlan]);

  const currencySymbol = getCurrencySymbol(currency);

  if (loading && plans.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Plan cards grid */}
      <div
        className="grid gap-4 justify-center mx-auto pt-3"
        style={{
          gridTemplateColumns: `repeat(${Math.min(plans.length, 4)}, minmax(0, 300px))`,
        }}
      >
        {plans.map((plan) => {
          const isSelected = value === plan.alias;
          const isRecommended = plan.alias === recommendedPlan;
          const isCurrent = plan.alias === currentPlan;
          const priceEntry = getPriceEntry(plan);
          const features = getFeatures(plan);
          // When yearly is selected, divide by 12 to show the per-month equivalent (still labelled "/month")
          const rawPrice = priceEntry?.price ?? 0;
          const displayPrice = isYearly ? rawPrice / 12 : rawPrice;
          const priceDollars = Math.floor(displayPrice);
          const priceCents = (displayPrice % 1).toFixed(2).slice(2);

          return (
            <div
              key={plan._id}
              role="radio"
              tabIndex={0}
              aria-checked={isSelected}
              className={cn(
                'relative flex flex-col rounded-lg border cursor-pointer transition-colors bg-card',
                isSelected
                  ? 'border-primary shadow-[0_12px_24px_-6px_rgba(0,0,0,0.18)]'
                  : 'border-border hover:border-primary',
              )}
              data-testid={`plan-card-${plan.alias ?? 'unknown'}`}
              onClick={() => onChange(plan.alias ?? '')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onChange(plan.alias ?? '');
                }
              }}
            >
              {/* Most Popular pill — pinned to the top edge, centered, overlapping the border.
                  Always primary teal; gains a white border when its card is selected so it
                  stands out against the teal header. */}
              {isRecommended && (
                <span
                  className={cn(
                    'absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center rounded-md bg-primary text-white text-xs font-semibold px-3 py-1 [border-width:1.5px] border-solid z-10',
                    isSelected ? 'border-white' : 'border-primary',
                  )}
                >
                  {t('Most Popular')}
                </span>
              )}

              {/* Card header — teal for selected plan, white for others.
                  Negative margins pull the header over the 1px border so the
                  fill reaches the rounded corner without a sliver showing through.
                  Applied to both states so the title position doesn't shift on selection. */}
              <div
                className={cn(
                  'px-4 pt-7 pb-4 text-center rounded-t-lg -mt-px -mx-px',
                  isSelected ? 'bg-primary text-white' : 'bg-transparent',
                )}
              >
                <div className="flex items-center justify-center gap-2">
                  <h3
                    className={cn(
                      'text-lg font-semibold',
                      isSelected ? 'text-white' : 'text-foreground',
                    )}
                  >
                    {plan.name || getPlanDisplayName(plan.alias ?? '')}
                  </h3>
                  {isCurrent && isTrial && (
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger
                          type="button"
                          onClick={(e) => e.stopPropagation()}
                          aria-label={t('Trial details')}
                          data-testid="plan-card-current-trial-badge"
                          className={cn(
                            'inline-flex items-center gap-1 rounded-sm px-2.5 py-1 text-xs font-semibold cursor-help',
                            'bg-foreground text-background',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                          )}
                        >
                          {t('Current Trial')}
                          <AlertCircle className="h-3.5 w-3.5" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          {trialEndDate
                            ? t(
                                'Your trial expires on {{date}}. Upgrade today to keep your screens running.',
                                { date: formatDateMedium(trialEndDate, '') },
                              )
                            : t('Upgrade today to keep your screens running.')}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>

              {/* Price */}
              <div className="px-4 py-4 text-center">
                <div className="flex items-start justify-center">
                  <span className="text-sm text-primary mt-2">{currencySymbol}</span>
                  <span className="text-4xl font-semibold text-primary leading-none">
                    {priceDollars}.
                  </span>
                  <span className="text-lg text-primary mt-1">{priceCents}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('screen')} / {t('month')}
                </p>
                <div className="mt-2 flex h-6 items-center justify-center">
                  {isYearly ? (
                    <p className="text-sm font-semibold text-foreground">
                      {t('{{total}} billed annually', {
                        total: `${currencySymbol}${rawPrice.toFixed(2)}`,
                      })}
                    </p>
                  ) : onBillingIntervalChange ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onBillingIntervalChange(BILLING_INTERVAL.YEARLY);
                      }}
                      aria-label={t('Switch to yearly billing')}
                      className="inline-flex items-center cursor-pointer rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 px-2.5 py-0.5 text-xs font-semibold transition-colors hover:bg-amber-200 dark:hover:bg-amber-950/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                    >
                      {t('Save 10% on yearly')}
                    </button>
                  ) : (
                    <Badge className="rounded-full bg-amber-100 text-amber-700 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-300 dark:hover:bg-amber-950/40">
                      {t('Save 10% on yearly')}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Features list */}
              <div className="flex-1 px-4 pb-2">
                <ul className="space-y-1.5">
                  {features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check
                        className={cn(
                          'h-4 w-4 shrink-0 mt-0.5',
                          feature.checked ? 'text-primary' : 'text-muted-foreground/40',
                        )}
                        aria-hidden
                      />
                      <span>{feature.description}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Learn More link */}
              <div className="px-4 text-center">
                <a
                  href={plan.learnMoreURL ?? 'https://www.optisigns.com/pricing'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary underline cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('Learn More')}
                </a>
              </div>

              {/* Select button — selects the card only; user advances manually with the wizard footer */}
              <div className="px-4 pb-4 pt-2 text-center">
                <Button
                  variant={isCurrent ? 'default' : 'outline'}
                  size="sm"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(plan.alias ?? '');
                  }}
                >
                  {isCurrent && !isTrial ? t('Current Plan') : t('Select')}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Free plan footer bar — full-width row with prompt on the left and Downgrade button on the right */}
      {!minPlanRequired && !hideFreePlanOption && onSelectFree && (
        <div className="flex w-full items-center justify-between gap-3 rounded-lg border border-muted-foreground/20 bg-muted/40 px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-foreground min-w-0">
            <span className="truncate">{t("Don't want to keep your current features?")}</span>
            <TooltipProvider delayDuration={100} skipDelayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={t('Free plan details')}
                    className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors inline-flex items-center justify-center p-1 -m-1 shrink-0"
                  >
                    <Info className="h-4 w-4 pointer-events-none" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <ul className="space-y-1 text-left">
                    {freePlanLimitations.map((feature, i) => (
                      <li key={`limit-${i}`} className="flex items-start gap-2">
                        <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                    {freePlanIncluded.map((feature, i) => (
                      <li key={`incl-${i}`} className="flex items-start gap-2">
                        <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={selectingFree}
            className={cn(
              'shrink-0 cursor-pointer bg-transparent hover:bg-foreground/5',
              selectingFree && 'opacity-50 cursor-not-allowed',
            )}
            onClick={onSelectFree}
            data-testid="plan-comparison-downgrade-free-btn"
          >
            {selectingFree ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t('Downgrade to Free Plan')}
              </>
            ) : (
              t('Downgrade to Free Plan')
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
